<template>
    <v-card class="elevation-4 rounded-lg overflow-hidden diagnostic-hub">
      <!-- Header Banner -->
      <v-sheet :style="gradientIconStyle" class="pa-4 white--text">
        <div class="d-flex align-center justify-space-between flex-wrap ga-2">
          <div>
            <!-- <div class="text-caption text-uppercase font-weight-bold grey--text text--lighten-2">
              
            </div> -->
            <h2 class="text-h6 font-weight-bold mb-0">
              {{ data.repair_procedure ? data.repair_procedure.title : 'AI Diagnostic & Technical Hub' }}
            </h2>
          </div>
          <v-chip
            v-if="data.dtc_analysis && data.dtc_analysis.code && data.dtc_analysis.code !== 'N/A'"
            color="error"
            label
            class="font-weight-bold"
          >
            <v-icon left small>mdi-engine-outline</v-icon>
            {{ data.dtc_analysis.code }}
          </v-chip>
        </div>
        <p v-if="data.summary" class="text-body-2 mb-0 mt-2 grey--text text--lighten-3">
          {{ data.summary }}
        </p>
      </v-sheet>
  
      <!-- Navigation Tabs for Mobile & Desktop -->
      <v-tabs
        v-model="activeTab"
        background-color="grey lighten-4"
        color="primary"
        grow
        show-arrows
      >
        <v-tab title="Diagnostics">
          <v-icon left small>mdi-stethoscope</v-icon>
          Diagnostics
        </v-tab>
        <v-tab title="Repair Guide">
          <v-icon left small>mdi-wrench</v-icon>
          Repair Guide
        </v-tab>
        <v-tab title="Wiring & Circuits">
          <v-icon left small>mdi-current-ac</v-icon>
          Wiring & Circuits
        </v-tab>
        <v-tab title="Parts & Tools">
          <v-icon left small>mdi-package-variant-closed</v-icon>
          Parts & Tools
        </v-tab>
        <v-tab title="Estimate Items">
          <v-icon left small>mdi-file-document-outline</v-icon>
          Invoice Line Items
        </v-tab>
      </v-tabs>
  
      <v-divider></v-divider>
  
      <!-- Content Windows -->
      <v-card-text class="pa-4">
        <v-tabs-items v-model="activeTab">
          
          <!-- TAB 1: DTC & DIAGNOSTIC FLOWCHART -->
          <v-tab-item>
            <div v-if="data.dtc_analysis">
              <h3 class="text-subtitle-1 font-weight-bold mb-2">
                <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
                Fault Description
              </h3>
              <v-alert type="warning" dense outlined class="mb-4 text-body-2">
                {{ data.dtc_analysis.description }}
              </v-alert>
  
              <!-- Probable Causes -->
              <h4 class="text-subtitle-2 font-weight-bold mb-2 grey--text text--darken-2">
                Probable Root Causes
              </h4>
              <div class="d-flex flex-wrap ga-2 mb-4">
                <v-chip
                  v-for="(cause, index) in data.dtc_analysis.probable_causes"
                  :key="'cause-' + index"
                  small
                  outlined
                  color="red lighten-1"
                  class="mb-1 mr-1"
                >
                  <v-icon left small>mdi-target</v-icon>
                  {{ cause }}
                </v-chip>
              </div>
  
              <!-- Diagnostic Steps Interactive List -->
              <h4 class="text-subtitle-2 font-weight-bold mb-2 grey--text text--darken-2">
                Step-by-Step Flowchart
              </h4>
              <v-list dense class="pa-0">
                <v-list-item-group v-model="completedSteps" multiple active-class="success--text text--decoration-line-through">
                  <v-list-item
                    v-for="(step, index) in data.dtc_analysis.diagnostic_steps"
                    :key="'dtc-step-' + index"
                    class="rounded-lg mb-2 border-step"
                  >
                    <template v-slot:default="{ active }">
                      <v-list-item-action class="mr-3">
                        <v-checkbox :input-value="active" color="success" hide-details></v-checkbox>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title class="text-body-2 text-wrap">
                          {{ step }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </div>
          </v-tab-item>
  
          <!-- TAB 2: REPAIR PROCEDURE & TORQUE SPECS -->
          <v-tab-item>
            <div v-if="data.repair_procedure">
              <h3 class="text-subtitle-1 font-weight-bold mb-3">
                {{ data.repair_procedure.title }}
              </h3>
              
              <!-- Torque Specifications Table -->
              <div v-if="data.repair_procedure.torque_specs && data.repair_procedure.torque_specs.length" class="mb-4">
                <v-subheader class="pa-0 font-weight-bold black--text">
                  <v-icon left color="primary">mdi-format-title</v-icon> Torque Specifications
                </v-subheader>
                <v-simple-table dense class="elevation-1 rounded-lg">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Component / Fastener</th>
                        <th class="text-right">Specification</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(spec, index) in data.repair_procedure.torque_specs" :key="'torque-' + index">
                        <td class="font-weight-medium">{{ spec.component }}</td>
                        <td class="text-right primary--text font-weight-bold">{{ spec.spec }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
  
              <!-- Repair Steps -->
              <v-subheader class="pa-0 font-weight-bold black--text mb-2">
                <v-icon left color="primary">mdi-numeric</v-icon> Procedure Steps
              </v-subheader>
              <v-timeline dense align-top class="pa-0">
                <v-timeline-item
                  v-for="(step, index) in data.repair_procedure.steps"
                  :key="'repair-step-' + index"
                  small
                  color="primary"
                >
                  <div class="text-body-2 text-wrap">
                    <span class="font-weight-bold mr-1">Step {{ index + 1 }}:</span> {{ step }}
                  </div>
                </v-timeline-item>
              </v-timeline>
            </div>
          </v-tab-item>
  
          <!-- TAB 3: WIRING & CIRCUITS -->
          <v-tab-item>
            <div v-if="data.wiring_and_circuits">
              <!-- Relevant Circuits -->
              <v-row>
                <v-col cols="12" md="6">
                  <h4 class="text-subtitle-2 font-weight-bold mb-2">Circuit Overview</h4>
                  <v-chip-group column>
                    <v-chip
                      v-for="(circuit, index) in data.wiring_and_circuits.relevant_circuits"
                      :key="'circ-' + index"
                      small
                      color="indigo lighten-5"
                      class="indigo--text font-weight-medium"
                    >
                      <v-icon left small color="indigo">mdi-flash</v-icon>
                      {{ circuit }}
                    </v-chip>
                  </v-chip-group>
  
                  <!-- Wire Color Codes / Pinouts -->
                  <h4 class="text-subtitle-2 font-weight-bold mt-4 mb-2">Pinout & Color Codes</h4>
                  <v-list dense class="elevation-1 rounded-lg pa-0">
                    <v-list-item
                      v-for="(pin, index) in data.wiring_and_circuits.wire_color_codes"
                      :key="'pin-' + index"
                      class="border-bottom"
                    >
                      <v-list-item-avatar size="14" class="mr-2">
                        <v-sheet class="rounded-circle fill-height width-100" :color="getWireColorHex(pin)"></v-sheet>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption font-weight-bold">{{ pin }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
  
                <!-- Dynamic Interactive Pinout/Circuit Visualizer -->
                <v-col cols="12" md="6">
                  <h4 class="text-subtitle-2 font-weight-bold mb-2">Interactive Harness Visualizer</h4>
                  <v-sheet color="grey darken-3" class="rounded-lg pa-3 text-center white--text">
                    <svg viewBox="0 0 300 120" width="100%" height="120" class="d-block mx-auto">
                      <!-- Connector Box -->
                      <rect x="20" y="20" width="260" height="80" rx="10" fill="#212121" stroke="#424242" stroke-width="2" />
                      <text x="150" y="38" fill="#9E9E9E" font-size="10" text-anchor="middle" font-family="sans-serif">HARNESS CONNECTOR VIEW</text>
                      
                      <!-- Generated Pins -->
                      <g v-for="(pin, index) in data.wiring_and_circuits.wire_color_codes" :key="'svg-pin-' + index">
                        <circle
                          :cx="45 + (index % 6) * 42"
                          :cy="index < 6 ? 60 : 85"
                          r="12"
                          :fill="getWireColorHex(pin)"
                          stroke="#FFFFFF"
                          stroke-width="1.5"
                          class="cursor-pointer pin-node"
                        />
                        <text
                          :cx="45 + (index % 6) * 42"
                          :x="45 + (index % 6) * 42"
                          :y="index < 6 ? 64 : 89"
                          fill="#000"
                          font-size="9"
                          font-weight="bold"
                          text-anchor="middle"
                        >
                          {{ index + 1 }}
                        </text>
                      </g>
                    </svg>
                    <div class="text-caption grey--text text--lighten-1 mt-1">
                      Tap pin node to highlight trace
                    </div>
                  </v-sheet>
  
                  <!-- Test Points -->
                  <h4 class="text-subtitle-2 font-weight-bold mt-4 mb-2">Diagnostic Test Points</h4>
                  <v-alert
                    v-for="(tp, index) in data.wiring_and_circuits.test_points"
                    :key="'tp-' + index"
                    type="info"
                    dense
                    outlined
                    class="text-caption mb-2"
                  >
                    {{ tp }}
                  </v-alert>
                </v-col>
              </v-row>
            </div>
          </v-tab-item>
  
          <!-- TAB 4: PARTS & TOOLS MANIFEST -->
          <v-tab-item>
            <div v-if="data.parts_and_tools">
              <v-row>
                <!-- Required Parts -->
                <v-col cols="12" md="6">
                  <h4 class="text-subtitle-2 font-weight-bold mb-2">Required OEM / Aftermarket Parts</h4>
                  <v-list dense class="elevation-1 rounded-lg pa-0">
                    <v-list-item
                      v-for="(part, index) in data.parts_and_tools.required_parts"
                      :key="'part-' + index"
                      class="border-bottom"
                    >
                      <v-list-item-icon class="mr-3">
                        <v-icon color="primary">mdi-cog</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold text-body-2">
                          {{ part.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          Part #: {{ part.oem_part_number || 'Generic Equivalent' }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip x-small color="grey lighten-2">Qty: {{ part.qty }}</v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-col>
  
                <!-- Specialty Tools Required -->
                <v-col cols="12" md="6">
                  <h4 class="text-subtitle-2 font-weight-bold mb-2">Specialty Shop Tools</h4>
                  <v-list dense class="elevation-1 rounded-lg pa-0">
                    <v-list-item
                      v-for="(tool, index) in data.parts_and_tools.specialty_tools"
                      :key="'tool-' + index"
                      class="border-bottom"
                    >
                      <v-list-item-icon class="mr-3">
                        <v-icon color="orange darken-2">mdi-tools</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-body-2 font-weight-medium">
                          {{ tool }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </div>
          </v-tab-item>
  
          <!-- TAB 5: INVOICE / ESTIMATE CONVERTER -->
          <v-tab-item>
            <div v-if="data.invoice_line_items">
              <div class="d-flex justify-space-between align-center mb-3">
                <h4 class="text-subtitle-1 font-weight-bold">Generated Job Estimate Line Items</h4>
                <v-btn color="success" small class="elevation-1" @click="addAllToInvoice">
                  <v-icon left small>mdi-plus-box-multiple</v-icon>
                  Add All to Invoice
                </v-btn>
              </div>
  
              <v-simple-table class="elevation-1 rounded-lg">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Description</th>
                      <th class="text-center">Qty / Hrs</th>
                      <th class="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in data.invoice_line_items" :key="'inv-item-' + index">
                      <td>
                        <v-chip
                          x-small
                          :color="item.type === 'labor' ? 'blue lighten-4' : 'amber lighten-4'"
                          :class="item.type === 'labor' ? 'blue--text text--darken-3' : 'amber--text text--darken-4'"
                          class="font-weight-bold"
                        >
                          {{ item.type.toUpperCase() }}
                        </v-chip>
                      </td>
                      <td class="font-weight-medium text-body-2">{{ item.description }}</td>
                      <td class="text-center font-weight-bold">
                        {{ item.estimated_hours || item.qty || 1 }}
                      </td>
                      <td class="text-right">
                        <v-btn icon color="primary" small @click="addSingleToInvoice(item)">
                          <v-icon small>mdi-plus-circle</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-tab-item>
  
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </template>
  
  <script>
  export default {
    name: 'DiagnosticKnowledgeHub',
    computed:{
        gradientIconStyle(){
            return this.$store.getters.gradientIconStyle
        }
    },
    props: {
      // The JSON payload returned from Gemini API matching the structured output spec
      data: {
        type: Object,
        required: true,
        default: () => ({})
      }
    },
    data() {
      return {
        activeTab: 0,
        completedSteps: []
      }
    },
    methods: {
      // Helper method to resolve wire colors safely (avoiding optional chaining)
      getWireColorHex(pinString) {
        if (!pinString) return '#9E9E9E';
        const str = pinString.toUpperCase();
        if (str.includes('RED')) return '#E53935';
        if (str.includes('BLK') || str.includes('BLACK')) return '#212121';
        if (str.includes('BLU') || str.includes('BLUE')) return '#1E88E5';
        if (str.includes('GRN') || str.includes('GREEN')) return '#43A047';
        if (str.includes('YEL') || str.includes('YELLOW')) return '#FDD835';
        if (str.includes('WHT') || str.includes('WHITE')) return '#FAFAFA';
        if (str.includes('ORG') || str.includes('ORANGE')) return '#FB8C00';
        if (str.includes('BRN') || str.includes('BROWN')) return '#6D4C41';
        return '#B0BEC5';
      },
  
      // Emit line items to parent invoice management engine
      addSingleToInvoice(item) {
        this.$emit('add-line-item', item);
      },
      addAllToInvoice() {
        if (this.data && this.data.invoice_line_items) {
          this.$emit('add-all-line-items', this.data.invoice_line_items);
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .diagnostic-hub {
    border: 1px solid rgba(0, 0, 0, 0.08);
  }
  .border-step {
    border: 1px solid #e0e0e0;
  }
  .border-bottom {
    border-bottom: 1px solid #f0f0f0;
  }
  .pin-node {
    transition: transform 0.2s ease, stroke-width 0.2s ease;
  }
  .pin-node:hover {
    transform: scale(1.15);
    stroke-width: 2.5px;
  }
  .fill-height {
    height: 100%;
  }
  .width-100 {
    width: 100%;
  }
  .cursor-pointer {
    cursor: pointer;
  }
  .ga-2 {
    gap: 8px;
  }
  </style>